/* ============================================================
   SettleTabDialog — Customer-side tab settle picker.

   Owner spec Feb 2026 (FINAL ORDERING WORKFLOW): when a customer
   taps the persistent SETTLE TAB button while their tab is
   active, they get three payment options:

      1. Pay At Bar – Save 10%
      2. EFTPOS
      3. Apple Pay

   Behaviour by option:
     • Pay At Bar  → POSTs a `pay_at_bar` service-request so the
       bartender gets a bell. Customer then sees a small
       confirmation screen telling them to show their name at
       the bar. Tab stays open until staff settle it from the
       Till (cash with 10%-off promo).
     • EFTPOS / Apple Pay → kicks off `/payments/tab-topup-checkout`
       for the FULL remaining balance. Square's hosted checkout
       page supports both EFTPOS card AND Apple Pay (iOS Safari)
       automatically. On webhook completion the backend auto-
       closes the tab and frees the table (see server.py
       `square_webhook` → tab_topup branch).

   This component owns the UI flow ONLY — payment infrastructure
   is the existing Square + service-request pipeline, untouched.
   ============================================================ */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Banknote, CreditCard, Apple, Loader2, CheckCircle, Bell } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function SettleTabDialog({ open, onOpenChange, tab, tableId, tableNumber, guestId, customerName, outstanding, tableTabs }) {
  const [step, setStep] = useState('choose'); // 'choose' | 'pay-at-bar-confirm' | 'eftpos-at-table-confirm' | 'redirecting'
  const [submitting, setSubmitting] = useState(false);
  // Owner ask Feb 2026: when there's more than one tab on the table, the
  // customer paying for the group needs a "Whole table" option. Default
  // is always their OWN tab to keep the regular case frictionless.
  const [scope, setScope] = useState('mine'); // 'mine' | 'table'

  // Reset back to the chooser whenever the modal re-opens so a customer
  // returning after a successful Square redirect doesn't see the old
  // "Redirecting…" spinner.
  React.useEffect(() => {
    if (open) { setStep('choose'); setScope('mine'); }
  }, [open]);

  // `outstanding` is the parent's authoritative figure for THIS customer's
  // tab (max of server-side `tab.current_total - paid_amount` and the
  // visible-orders running total — see CustomerPage.tabRunningTotal).
  // Falls back to server math so the dialog still works in isolation.
  const fallback = Math.max(
    0,
    Number(tab?.current_total || 0) - Number(tab?.paid_amount || 0)
  );
  const myOwed = typeof outstanding === 'number' && outstanding > 0
    ? outstanding
    : fallback;

  // Whole-table totals. Sibling tabs come from CustomerPage's
  // /tables/overview poll. We deliberately re-include the customer's own
  // tab from the aggregate if it's there (server may not yet reflect
  // the very latest mutation) and clamp >= myOwed so we never undersell.
  const siblingTotal = (tableTabs || [])
    .filter((t) => t.id !== tab?.id)
    .reduce((s, t) => s + Math.max(0, Number(t.current_total || 0) - Number(t.paid_amount || 0)), 0);
  const tableOwed = Math.max(myOwed + siblingTotal, myOwed);
  const otherTabCount = (tableTabs || []).filter((t) => t.id !== tab?.id).length;
  const canScopeTable = otherTabCount > 0 && tableOwed > myOwed;

  const owed = scope === 'table' ? tableOwed : myOwed;
  const owedCash = +(owed * 0.9).toFixed(2); // 10% off shown as the cash-at-bar headline price

  const handleStaffCollect = async ({ kind, label, confirmStep }) => {
    if (!tableId) {
      toast.error('Table not detected — please refresh and try again.');
      return;
    }
    setSubmitting(true);
    try {
      // Ring the bell with the appropriate request_type so the till + bar
      // staff see what kind of settlement is needed:
      //   • pay_at_bar      — customer coming up with cash (10% promo)
      //   • payment_eftpos  — staff brings the EFTPOS machine to table
      // Both reuse the existing /service-requests pipeline (debounced,
      // multi-staff broadcast). Tab stays open until staff settles on the
      // till. NO Square redirect, NO online payment — same as the prior
      // deployment's "pay at the bar" mental model, just with EFTPOS as
      // a second option for customers who don't want to walk up.
      const scopeLabel = scope === 'table'
        ? `WHOLE TABLE (T${tableNumber || '?'} · ${otherTabCount + 1} tabs)`
        : (tab?.customer_name || customerName || 'Guest');
      await api.post('/service-requests', {
        table_id: tableId,
        customer_name: customerName || 'Guest',
        guest_id: guestId || null,
        request_type: kind,
        note: `Settling tab via ${label} · ${scopeLabel} · $${owed.toFixed(2)}`,
      });
      setStep(confirmStep);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && /already/i.test(String(detail))) {
        setStep(confirmStep);
      } else {
        toast.error(detail || 'Could not notify staff. Please walk up to the bar.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayAtBar = () =>
    handleStaffCollect({ kind: 'pay_at_bar', label: 'cash at the bar', confirmStep: 'pay-at-bar-confirm' });

  const handleEftposAtTable = () =>
    handleStaffCollect({ kind: 'payment_eftpos', label: 'EFTPOS at the table', confirmStep: 'eftpos-at-table-confirm' });

  const handleApplePay = async () => {
    if (!tab?.id) {
      toast.error('Tab not loaded — try again in a moment.');
      return;
    }
    if (owed <= 0) {
      toast.error('No outstanding balance on your tab.');
      return;
    }
    setSubmitting(true);
    setStep('redirecting');
    try {
      // /payments/tab-topup-checkout returns a Square hosted-checkout URL.
      // On iOS Safari Square's page auto-renders the Apple Pay button;
      // on other browsers it falls back to card entry. We pass the full
      // owed amount so the webhook auto-closes the tab once paid.
      // EFTPOS is intentionally NOT routed through here — staff prefer to
      // run their countertop terminal for traceability + tips handling.
      const origin =
        typeof window !== 'undefined' && window.location ? window.location.origin : '';
      const { data } = await api.post(`/payments/tab-topup-checkout/${tab.id}`, {
        amount: owed,
        origin_url: origin,
        guest_id: guestId,
      });
      if (data?.checkout_url) {
        try {
          if (typeof window !== 'undefined' && window.location) {
            localStorage.setItem('majnu:lastReturnPath', window.location.pathname);
          }
        } catch {
          /* private mode — ignore */
        }
        window.location.href = data.checkout_url;
        return;
      }
      throw new Error('No checkout URL returned');
    } catch (err) {
      setSubmitting(false);
      setStep('choose');
      toast.error(
        err?.response?.data?.detail ||
          "Apple Pay couldn't start — please try EFTPOS or pay at the bar."
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent
        className="bg-bg-secondary border border-white/10 max-w-md text-white"
        data-testid="settle-tab-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-white">
            {step === 'pay-at-bar-confirm'
              ? 'See you at the bar'
              : step === 'eftpos-at-table-confirm'
                ? 'Staff are on the way'
                : 'Choose Payment Method'}
          </DialogTitle>
        </DialogHeader>

        {step === 'choose' && (
          <div className="space-y-3 mt-2">
            {/* Scope toggle — only renders when there are sibling tabs on
                the same table (someone covering the whole group). */}
            {canScopeTable && (
              <div
                className="flex items-center bg-zinc-900 border border-white/10 rounded-md p-0.5"
                data-testid="settle-scope-toggle"
              >
                <button
                  type="button"
                  onClick={() => setScope('mine')}
                  className={`flex-1 h-9 text-[11px] font-sans uppercase tracking-[0.12em] rounded transition ${
                    scope === 'mine'
                      ? 'bg-gold text-black'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="settle-scope-mine"
                >
                  Just my tab · ${myOwed.toFixed(2)}
                </button>
                <button
                  type="button"
                  onClick={() => setScope('table')}
                  className={`flex-1 h-9 text-[11px] font-sans uppercase tracking-[0.12em] rounded transition ${
                    scope === 'table'
                      ? 'bg-gold text-black'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="settle-scope-table"
                >
                  Whole table · ${tableOwed.toFixed(2)}
                </button>
              </div>
            )}

            {/* Amount owed — informational */}
            <div className="flex items-center justify-between rounded-sm border border-white/10 bg-white/[0.03] px-3 py-2.5">
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-sans">
                  Outstanding
                </p>
                <p className="text-zinc-300 text-[11px] font-sans mt-0.5">
                  {scope === 'table'
                    ? `Whole table · ${otherTabCount + 1} tabs`
                    : (tab?.customer_name) ? `${tab.customer_name}'s tab` : 'Your tab'}
                </p>
              </div>
              <span className="font-mono text-xl text-gold" data-testid="settle-tab-amount">
                ${owed.toFixed(2)}
              </span>
            </div>

            {/* 1. Pay At Bar — Save 10% */}
            <button
              type="button"
              onClick={handlePayAtBar}
              disabled={submitting}
              className="relative w-full text-left rounded-sm border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/15 px-4 py-3.5 transition disabled:opacity-60"
              data-testid="settle-tab-pay-at-bar"
            >
              <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded-sm text-[9px] font-bold tracking-[0.08em] uppercase bg-emerald-500 text-black">
                Save 10%
              </span>
              <div className="flex items-center gap-3">
                <Banknote className="w-5 h-5 text-emerald-300 flex-shrink-0" />
                <div>
                  <p className="text-emerald-300 font-sans text-sm font-semibold">Pay Cash At Bar</p>
                  <p className="text-zinc-400 font-sans text-[11px] mt-0.5">
                    Bar charges <span className="text-emerald-300 font-mono">${owedCash.toFixed(2)}</span> when you pay cash at the counter
                  </p>
                </div>
              </div>
            </button>

            {/* 2. EFTPOS — staff brings the EFTPOS terminal to the
                table (mirrors the previous deployment's behaviour). No
                online charge here; tab stays open until the bartender
                completes the transaction on their countertop terminal
                and taps Settle. */}
            <button
              type="button"
              onClick={handleEftposAtTable}
              disabled={submitting}
              className="w-full text-left rounded-sm border border-white/15 bg-white/[0.03] hover:border-gold/40 hover:bg-gold/[0.05] px-4 py-3.5 transition disabled:opacity-60"
              data-testid="settle-tab-eftpos"
            >
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-gold flex-shrink-0" />
                <div>
                  <p className="text-white font-sans text-sm font-semibold">EFTPOS</p>
                  <p className="text-zinc-500 font-sans text-[11px] mt-0.5">
                    Staff will bring the EFTPOS machine to your table
                  </p>
                </div>
              </div>
            </button>

            {/* 3. Apple Pay — Square hosted checkout, instant tab close.
                Only available when settling MY tab — the Square checkout
                endpoint settles a single tab_id. For whole-table the
                customer must use Pay At Bar or EFTPOS so staff can run
                the multi-tab close on the till. */}
            <button
              type="button"
              onClick={scope === 'table' ? undefined : handleApplePay}
              disabled={submitting || scope === 'table'}
              title={scope === 'table' ? 'Available for individual tab only — staff close the whole table on the till' : ''}
              className={`w-full text-left rounded-sm border border-white/15 px-4 py-3.5 transition ${
                scope === 'table'
                  ? 'bg-black/40 opacity-50 cursor-not-allowed'
                  : 'bg-black hover:bg-zinc-900 disabled:opacity-60'
              }`}
              data-testid="settle-tab-apple-pay"
            >
              <div className="flex items-center gap-3">
                <Apple className="w-5 h-5 text-white flex-shrink-0" />
                <div>
                  <p className="text-white font-sans text-sm font-semibold">Apple Pay</p>
                  <p className="text-zinc-400 font-sans text-[11px] mt-0.5">
                    {scope === 'table'
                      ? 'Pick Pay Cash At Bar / EFTPOS for the whole table'
                      : <>Pay <span className="font-mono">${owed.toFixed(2)}</span> with Face ID / Touch ID</>}
                  </p>
                </div>
              </div>
            </button>

            {submitting && step === 'choose' && (
              <div className="flex items-center justify-center gap-2 text-zinc-400 text-xs pt-1">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Notifying the bar…
              </div>
            )}
          </div>
        )}

        {step === 'pay-at-bar-confirm' && (
          <div className="space-y-4 mt-2" data-testid="settle-tab-bar-confirm">
            <div className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/40">
              <Bell className="w-7 h-7 text-emerald-300" />
            </div>
            <div className="text-center">
              <p className="text-white font-serif text-base mb-1">The bar knows you&apos;re coming</p>
              <p className="text-zinc-400 font-sans text-xs leading-relaxed px-2">
                Show your name <span className="text-emerald-300 font-semibold">{customerName || tab?.customer_name || 'Guest'}</span> at the counter. Pay cash and the team applies your <span className="text-emerald-300 font-semibold">10% off</span> on the spot.
              </p>
            </div>
            <div className="rounded-sm border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-emerald-300/80 font-sans">
                  Cash total
                </p>
                <p className="text-zinc-500 text-[10px] font-sans mt-0.5 line-through">
                  ${owed.toFixed(2)}
                </p>
              </div>
              <span className="font-mono text-2xl text-emerald-300">${owedCash.toFixed(2)}</span>
            </div>
            <Button
              onClick={() => onOpenChange(false)}
              className="w-full h-11 bg-gold text-black font-bold rounded-sm hover:bg-gold-light font-sans"
              data-testid="settle-tab-bar-confirm-close"
            >
              Got it
            </Button>
          </div>
        )}

        {step === 'eftpos-at-table-confirm' && (
          <div className="space-y-4 mt-2" data-testid="settle-tab-eftpos-confirm">
            <div className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-gold/10 border border-gold/40">
              <CreditCard className="w-7 h-7 text-gold" />
            </div>
            <div className="text-center">
              <p className="text-white font-serif text-base mb-1">EFTPOS coming to you</p>
              <p className="text-zinc-400 font-sans text-xs leading-relaxed px-2">
                A staff member will bring the EFTPOS machine to your table shortly. Sit tight — they&apos;ll take the card payment of <span className="text-gold font-mono font-semibold">${owed.toFixed(2)}</span> right there.
              </p>
            </div>
            <div className="rounded-sm border border-gold/30 bg-gold/[0.05] px-4 py-3 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.16em] text-gold/80 font-sans">
                Card total
              </span>
              <span className="font-mono text-2xl text-gold">${owed.toFixed(2)}</span>
            </div>
            <Button
              onClick={() => onOpenChange(false)}
              className="w-full h-11 bg-gold text-black font-bold rounded-sm hover:bg-gold-light font-sans"
              data-testid="settle-tab-eftpos-confirm-close"
            >
              Got it
            </Button>
          </div>
        )}

        {step === 'redirecting' && (
          <div className="text-center space-y-3 mt-2 py-4" data-testid="settle-tab-redirecting">
            <Loader2 className="w-8 h-8 text-gold mx-auto animate-spin" />
            <p className="text-zinc-300 font-sans text-sm">Opening secure payment…</p>
            <p className="text-zinc-500 font-sans text-[10px]">You&apos;ll come back here once it&apos;s done.</p>
          </div>
        )}

        {/* "Why pay at the bar?" footnote — small, secondary */}
        {step === 'choose' && (
          <div className="border-t border-white/5 pt-3 mt-2 flex items-start gap-2 text-zinc-500 text-[10px] font-sans leading-relaxed">
            <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0 text-emerald-500/70" />
            <span>
              No deposit was ever taken. Pick whichever way is easier — cash at the bar
              saves 10%, EFTPOS / Apple Pay close the tab instantly from here.
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
